/**
 * Persona chooser — /app/landing
 *
 * The five persona landing pages collapse 60+ navigable surfaces into
 * one role-shaped view. This page is the first-time-user funnel: it
 * lists the personas, lets the user pick one, and remembers the choice
 * in localStorage so subsequent visits to /app/landing redirect
 * straight to the saved persona.
 *
 * If the user already set a default, we redirect immediately. Otherwise
 * we render a 5-card picker, highlighting the persona suggested for
 * their role (returned by /api/personas).
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Sparkles, Shield, Scale, Eye, Activity, Star, ChevronRight,
  Briefcase, GitMerge, Server, LifeBuoy,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Persona {
  id: string
  label: string
  icon: string
  description: string
  suggested_for: string[]
  route: string
}

const ICON_MAP: Record<string, React.ElementType> = {
  Sparkles, Shield, Scale, Eye, Activity,
  Briefcase, GitMerge, Server, LifeBuoy,
}

const STORAGE_KEY = 'cdm-persona'
// Keep in sync with backend/api/personas.py — used to validate the
// localStorage default before redirecting (we don't want a stale id from
// a removed persona to send the user to a 404).
const KNOWN_PERSONA_IDS = [
  'cto', 'cio', 'ciso', 'compliance', 'soc',
  'sdlc', 'hosting', 'helpdesk', 'devops',
]

export default function PersonaChooserPage() {
  const navigate = useNavigate()
  const [personas, setPersonas] = useState<Persona[]>([])
  const [userRole, setUserRole] = useState<string>('viewer')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Honour the saved default before doing anything else — if you've
    // already chosen a persona, we don't want to flash the chooser.
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && KNOWN_PERSONA_IDS.includes(saved)) {
      navigate(`/app/landing/${saved}`, { replace: true })
      return
    }
    api.get('/api/personas')
      .then(({ data }) => {
        setPersonas(data?.personas || [])
        setUserRole(data?.user_role || 'viewer')
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [navigate])

  const handleChoose = (id: string, setDefault: boolean) => {
    if (setDefault) localStorage.setItem(STORAGE_KEY, id)
    navigate(`/app/landing/${id}`)
  }

  const suggested = personas.find(p => p.suggested_for.includes(userRole))?.id

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-text-muted text-sm">
        Loading personas...
      </div>
    )
  }

  return (
    <div className="space-y-5 animate-fade-in max-w-5xl mx-auto">
      <div className="pb-2 border-b border-border">
        <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
          <Sparkles size={20} className="text-blue-light" /> Pick Your Landing Page
        </h1>
        <p className="text-[11px] text-text-secondary mt-1">
          The CTODashboard ships 60+ pages. Pick the one curated for your role and we'll
          surface only the data you actually look at. You can change this any time.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {personas.map(p => {
          const Icon = ICON_MAP[p.icon] || Sparkles
          const isSuggested = p.id === suggested
          return (
            <div key={p.id}
              className={clsx(
                'card p-4 flex flex-col gap-3 border transition',
                isSuggested ? 'border-blue/40 ring-1 ring-blue/30' : 'border-border hover:border-blue/30',
              )}>
              <div className="flex items-start gap-2">
                <div className="w-9 h-9 rounded-lg bg-blue/10 border border-blue/30 flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-blue-light" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-bold text-text-primary flex items-center gap-1.5">
                    {p.label}
                    {isSuggested && (
                      <span className="badge text-[8px] bg-blue/20 text-blue-light border border-blue/30">
                        suggested for {userRole}
                      </span>
                    )}
                  </h2>
                </div>
              </div>
              <p className="text-[11px] text-text-secondary leading-snug">{p.description}</p>
              <div className="flex items-center justify-between gap-2 mt-auto pt-1">
                <button type="button" onClick={() => handleChoose(p.id, true)}
                  className="inline-flex items-center gap-1 text-[10px] text-text-muted hover:text-text-primary"
                  title="Save as default — visiting /app/landing skips this chooser next time">
                  <Star size={10} /> set as my default
                </button>
                <button type="button" onClick={() => handleChoose(p.id, false)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue text-white text-[10px] font-semibold hover:bg-blue/90">
                  Open <ChevronRight size={11} />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Default persona stored in localStorage as <code className="font-mono">{STORAGE_KEY}</code>.
        Clear it from <Link to="/app/settings" className="text-blue-light hover:underline">Settings</Link> if
        you want the chooser back.
      </p>
    </div>
  )
}
